import { getCategoryAndProductId } from 'analytics/snowplow/helpers/itemCategorisation'
import { dateDifference } from 'lib/datetime/dateUtils'
import { comluxgroup } from '@luxuryescapes/contract-data-event-schemas'
import { OFFER_TYPE_ALWAYS_ON } from 'constants/offer'
import { filterDefinedProperties } from 'analytics/snowplow/helpers'
import {
  isBedbankOffer,
  isCarHireOffer,
  isFlightDeal,
  isJourneyV2,
  isMerchandiseOffer,
} from 'lib/offer/offerTypes'
import { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import moment from 'moment'

function getGeneralOfferItem(
  otherOffer: App.ExperienceOffer | App.Tours.TourV2Offer | App.CruiseOffer | App.FlightDeal | App.JourneyV2 | App.CarHireOffer | App.MerchandiseOffer,
  state: App.State,
  variationId: string | null = null,
  offerPageState?: OfferPageState,
) {
  let offerLeadPrice: number | null = null
  let offerPrice: number | null = null
  let offerLeadUnit: string | null = null
  let offerLeadDuration: string | null = null
  let destinationCity: string | null = null
  let destinationCountry: string | null = null
  let originCity: string | null = null
  let originCountry: string | null = null
  let travelStart: string | null = null

  if (isCarHireOffer(otherOffer)) {
    return filterDefinedProperties([comluxgroup.createItem_1_1_1({
      offerId: otherOffer.id,
      categoryId: otherOffer.type,
      productId: otherOffer.type,
      offerLeadPrice: otherOffer.price,
      price: otherOffer.price,
      currency: state.geo.currentCurrency,
      offerLeadDuration: String(otherOffer.duration),
    })])
  }

  if (isFlightDeal(otherOffer)) {
    return filterDefinedProperties([comluxgroup.createItem_1_1_1({
      offerId: otherOffer.id,
      categoryId: 'flight',
      productId: 'flight',
      offerLeadPrice: otherOffer.perAdultPrice,
      price: otherOffer.perAdultPrice,
      currency: state.geo.currentCurrency,
      offerLeadDuration: otherOffer.travelDays,
    })])
  }

  if (isJourneyV2(otherOffer)) {
    return filterDefinedProperties([comluxgroup.createItem_1_1_1({
      offerId: otherOffer.id,
      categoryId: 'flight',
      productId: 'flight',
      offerLeadPrice: otherOffer.price.all.baseFare,
      price: otherOffer.price.all.baseFare,
      currency: state.geo.currentCurrency,
    })])
  }

  if (isMerchandiseOffer(otherOffer)) {
    return filterDefinedProperties([comluxgroup.createItem_1_1_1({
      offerId: otherOffer.id,
      categoryId: 'shop',
      productId: 'merchandise',
      offerLeadPrice: otherOffer.variants[0].price,
      price: otherOffer.variants[0].price,
      currency: state.geo.currentCurrency,
    })])
  }
  const { categoryId, productId } = getCategoryAndProductId(otherOffer.parentType, otherOffer)

  if (otherOffer.parentType === 'cruise') {
    offerLeadDuration = String(otherOffer.duration)
    if (otherOffer.lowestDeparturePrice) {
      offerLeadPrice = otherOffer.lowestDeparturePrice.total
      offerPrice = otherOffer.lowestDeparturePrice.total
    }

    const destinationCityAndCountry = otherOffer.returnPort.split(', ')
    const originCityAndCountry = otherOffer.departurePort.split(', ')
    if (destinationCityAndCountry.length > 1) {
      destinationCity = destinationCityAndCountry[0]
      destinationCountry = destinationCityAndCountry[1]
    }
    if (originCityAndCountry.length > 1) {
      originCity = originCityAndCountry[0]
      originCountry = originCityAndCountry[1]
    }
  }

  if (otherOffer.parentType === 'tour_v2') {
    const purchasableOptions = otherOffer.purchasableOptions
    if (purchasableOptions.length > 0) {
      const offerLead = purchasableOptions[0]

      const departure = otherOffer.departures[offerLead.fkDepartureId]
      const startDate = departure.startDate
      const endDate = departure.endDate

      // Sum 1 on days result because tours include the last day as duration too
      offerLeadDuration = (dateDifference(new Date(endDate), new Date(startDate)).days + 1).toString()
      offerLeadPrice = offerLead.price
      offerPrice = offerLead.price

      const departureMonth = offerPageState?.departureMonth?.month
      const departureYear = offerPageState?.departureMonth?.year
      const travelStartDate = departureMonth !== undefined && departureYear !== undefined ? new Date(Date.UTC(departureYear, departureMonth, 1)) : null
      travelStart = travelStartDate?.toISOString()?.split('T')[0] ?? null
    }

    offerLeadUnit = otherOffer.saleUnit

    if (variationId) {
      const offerVariations = otherOffer.variations[variationId]

      destinationCity = offerVariations?.endLocation ?? null
      destinationCountry = offerVariations?.countriesVisited?.length > 0 ? offerVariations.countriesVisited[0] : null
      originCity = offerVariations?.startLocation ?? null
      originCountry = offerVariations?.countriesVisited?.length > 0 ? offerVariations.countriesVisited.at(offerVariations.countriesVisited.length - 1) ?? null : null
    }
  }

  if (otherOffer.parentType === 'experience') {
    // In some scenarios it could be flexible but analytics teams said to keep duration 1
    offerLeadDuration = '1'
    offerLeadPrice = otherOffer.price
    offerPrice = otherOffer.price
    offerLeadUnit = otherOffer.ticketUnitLabel ?? null
    destinationCity = otherOffer.location.name
    originCity = otherOffer.location.name
  }

  const rooms = offerPageState?.rooms?.length ? offerPageState.rooms : [undefined]
  return filterDefinedProperties(rooms.map((room: App.RoomOccupants | undefined) => comluxgroup.createItem_1_1_1({
    offerId: otherOffer.id,
    categoryId,
    productId,
    offerLeadPrice,
    price: offerPrice,
    offerLeadUnit,
    offerLeadDuration,
    currency: state.geo.currentCurrency,
    destinationCity,
    destinationCountry,
    originCity,
    originCountry,

    /* Dynamic Data */
    travelStart,
    numberOfAdults: room?.adults ? room.adults : undefined,
    numberOfChildren: room?.children ? room.children : undefined,
    numberOfInfants: room?.infants ? room.infants : undefined,
    childrenAges: room?.childrenAge?.length ? room.childrenAge.filter(age => age >= 0) : undefined,
  })))
}

interface GetHotelOrTourOfferItemParams {
  hotelOrTourOffer: App.Offer | App.BedbankOffer | App.OfferSummary | App.BedbankOfferSummary
  state: App.State
  price?: number
  memberPrice?: number
  duration?: number
  offerPageState?: OfferPageState
  productAvailable?: boolean
  roomRateId?: string
  metadata?: string
  addPricesToMetaData?: boolean
}

function getHotelOrTourOfferItem({
  hotelOrTourOffer,
  state,
  price,
  memberPrice,
  duration,
  offerPageState,
  productAvailable,
  roomRateId,
  metadata,
  addPricesToMetaData = false,
}: GetHotelOrTourOfferItemParams) {
  const offerId = hotelOrTourOffer.id

  const { categoryId, productId } = getCategoryAndProductId(hotelOrTourOffer.parentType, hotelOrTourOffer)

  let offerLowestPricePackage: App.Package | undefined
  let offerGeoData: App.OfferPropertyGeoData | undefined

  if (hotelOrTourOffer.type !== 'bedbank_hotel') {
    offerLowestPricePackage = hotelOrTourOffer.lowestPricePackage
    offerGeoData = hotelOrTourOffer.property?.geoData
  }

  const offerPrice = productAvailable !== undefined ? price : offerLowestPricePackage?.price
  const offerLeadDuration = productAvailable !== undefined ? duration?.toString() : offerLowestPricePackage?.duration?.toString()
  const checkInDate = offerPageState?.checkInDate
  const checkOutDate = offerPageState?.checkOutDate
  let selectedDuration = offerPageState?.duration
  if (isBedbankOffer(hotelOrTourOffer)) {
    selectedDuration = checkInDate && checkOutDate ? moment(checkOutDate).diff(checkInDate, 'days') : undefined
  }
  const rooms = offerPageState?.rooms?.length ? offerPageState.rooms : [undefined]

  const itemMetaData = addPricesToMetaData ?
      JSON.stringify({ memberPrice, nonMemberPrice: offerPrice }) :
    metadata

  return filterDefinedProperties(rooms.map((room: App.RoomOccupants | undefined) => comluxgroup.createItem_1_1_1({
    offerId,
    rateId: roomRateId,
    categoryId,
    productId,
    price: offerPrice,
    offerLeadPrice: offerPrice, // This is to be deprecated by CRO
    offerLeadDuration,
    offerLeadUnit: hotelOrTourOffer.saleUnit,
    currency: state.geo.currentCurrency,
    destinationCity: offerGeoData?.locality,
    destinationRegion: offerGeoData?.administrativeAreaLevel1,
    destinationCountry: offerGeoData?.country,

    /* Dynamic Data */
    duration: selectedDuration,
    travelStart: checkInDate,
    travelEnd: checkOutDate,
    numberOfAdults: room?.adults ? room.adults : undefined,
    numberOfChildren: room?.children ? room.children : undefined,
    numberOfInfants: room?.infants ? room.infants : undefined,
    childrenAges: room?.childrenAge?.length ? room.childrenAge.filter(age => age >= 0) : undefined,
    metadata: itemMetaData,
  })))
}

function getHotelOrTourOfferItemWithAccommodation(offer: App.Offer | App.BedbankOffer | App.OfferSummary | App.BedbankOfferSummary, state: App.State) {
  const itemsContext = getHotelOrTourOfferItem({ hotelOrTourOffer: offer, state })
  const propertyMapping = state.offer.propertyOfferMappings[offer.id]?.mapping
  if (propertyMapping) {
    const {
      propertyId: accommodationId,
      lpcOfferIds,
      bedbankPropertyIds,
    } = propertyMapping
    itemsContext.push(...lpcOfferIds.map(offerId => comluxgroup.createAccommodation_1_0_0({
      accommodationId,
      offerId,
      // TODO(HP-1329): Distinguish between TAO and Flash offers.
      productId: OFFER_TYPE_ALWAYS_ON,
    })))
    itemsContext.push(...bedbankPropertyIds.map(offerId => comluxgroup.createAccommodation_1_0_0({
      accommodationId,
      offerId,
      productId: 'bedbank',
    })))
  }
  return itemsContext
}

export {
  getGeneralOfferItem,
  getHotelOrTourOfferItem,
  getHotelOrTourOfferItemWithAccommodation,
}
